import axios from 'axios';
import { createAuthHeaders } from '../api-client';
import { OnboardingResponse, MarkStepResponse } from '@optidev/shared-types';
import { analytics } from '@lib/analytics';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export const onboardingApi = {
  /**
   * Resolve the activation markers. No longer drives UI; the response backfills
   * missing marker timestamps server-side and reports which marks are still owed.
   */
  async getProgress(token: string): Promise<OnboardingResponse> {
    const response = await axios.get(`${API_URL}/api/users/me/onboarding`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Mark first project created (Step 1). Idempotent.
   */
  async markFirstProject(token: string): Promise<MarkStepResponse> {
    const response = await axios.post(
      `${API_URL}/api/users/me/onboarding/mark-first-project`,
      {},
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Mark first edit made (Step 2). Idempotent.
   */
  async markFirstEdit(token: string): Promise<MarkStepResponse> {
    const response = await axios.post(
      `${API_URL}/api/users/me/onboarding/mark-first-edit`,
      {},
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Mark first publish (Step 3). Idempotent.
   */
  async markFirstPublish(token: string): Promise<MarkStepResponse> {
    const response = await axios.post(
      `${API_URL}/api/users/me/onboarding/mark-first-publish`,
      {},
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

};

/**
 * Simple event bus for onboarding step completion.
 * Allows non-React code (API files) to notify the React context to refetch.
 * Module-level singleton in Vite SPA (evaluated once).
 */
type OnboardingListener = () => void;
const _listeners = new Set<OnboardingListener>();

export const onboardingEvents = {
  subscribe(fn: OnboardingListener): () => void {
    _listeners.add(fn);
    return () => _listeners.delete(fn);
  },
  _emit(): void {
    _listeners.forEach((fn) => fn());
  },
};

/**
 * In-flight guard — prevents duplicate concurrent calls for the same step.
 * Module-level singleton in Vite SPA (evaluated once).
 */
const _inFlight = new Set<string>();

/**
 * Fire-and-forget helper for marking onboarding steps.
 * 3-layer dedup: user-scoped localStorage + in-flight Set + backend idempotency.
 * Analytics events only fire when `response.marked === true` (newly marked),
 * ensuring cross-browser dedup via backend state.
 * Emits onboardingEvents on success so the React context can refetch (once per step).
 *
 * @param step - Which step to mark ('step1' | 'step3')
 * @param token - Firebase auth token
 * @param uid - Firebase UID for user-scoped localStorage key
 * @param eventData - Optional data to include in analytics events (e.g. { projectId })
 */
export function markStepIfNeeded(
  step: 'step1' | 'step3',
  token: string,
  uid: string,
  eventData?: Record<string, unknown>,
): void {
  const key = `onb:${uid}:${step}_marked`;

  // Layer 1: localStorage (persists across sessions)
  if (localStorage.getItem(key)) return;

  // Layer 2: in-flight guard (prevents rapid double-fire)
  if (_inFlight.has(key)) return;
  _inFlight.add(key);

  const call = step === 'step1'
    ? onboardingApi.markFirstProject(token)
    : onboardingApi.markFirstPublish(token);

  call
    .then((response) => {
      // Only cache locally once the server actually holds the marker. A 200 with
      // marked:false AND no timestamp means the EXISTS validation rejected the
      // call (e.g. the project row wasn't visible yet) — caching that would trip
      // the layer-1 short-circuit above on every later attempt and strand the
      // step as incomplete forever, including after "Create new project".
      if (response.marked || response.timestamp) {
        localStorage.setItem(key, 'true');
      }
      onboardingEvents._emit();

      // Only fire analytics when backend confirms this is truly the first time
      // (marked === true). Prevents duplicate events across browsers/devices.
      if (!response.marked) return;

      if (step === 'step1') {
        analytics.track('first_project_created');
      } else if (step === 'step3') {
        analytics.track('first_project_published', {
          ...eventData,
          $set_once: { first_published_at: new Date().toISOString() },
        });
      }
    })
    .catch(() => {})
    .finally(() => _inFlight.delete(key));
}
